import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addMembers, searchUsers } from '../api/chats';
import { listContacts } from '../api/contacts';
import { ApiError } from '../api/client';
import { backdropClose } from '../lib/modalBackdrop';

interface FoundUser {
  id: string;
  username: string;
  displayName: string;
}

interface Props {
  chatId: string;
  existingIds: Set<string>;
  onClose: () => void;
  onAdded: () => void;
  onInvite?: () => void;
}

/** Добавление участников требует явного подтверждения. */
export default function AddMembersModal({ chatId, existingIds, onClose, onAdded, onInvite }: Props) {
  const { t } = useTranslation('misc');
  const [q, setQ] = useState('');
  const [contacts, setContacts] = useState<FoundUser[]>([]);
  const [results, setResults] = useState<FoundUser[] | null>(null);
  const [selected, setSelected] = useState<FoundUser[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    listContacts().then((list) => setContacts(list as unknown as FoundUser[])).catch(() => {});
  }, []);

  useEffect(() => {
    const text = q.trim();
    if (!text) {
      setResults(null);
      return;
    }
    const t = window.setTimeout(() => {
      searchUsers(text).then(setResults).catch(() => setResults([]));
    }, 300);
    return () => window.clearTimeout(t);
  }, [q]);

  const toggle = (u: FoundUser) =>
    setSelected((prev) => (prev.some((s) => s.id === u.id) ? prev.filter((s) => s.id !== u.id) : [...prev, u]));

  const apply = async () => {
    if (busy) return;
    if (selected.length === 0) {
      onClose();
      return;
    }
    setBusy(true);
    setError('');
    try {
      await addMembers(chatId, selected.map((s) => s.id));
      onAdded();
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('addMembersFailed'));
      setBusy(false);
    }
  };

  const list = (results ?? contacts).filter((u) => !existingIds.has(u.id));

  return (
    <div className="modal-backdrop" {...backdropClose(onClose)}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" title={t('addMembersClose')} onClick={onClose} disabled={busy}>✕</button>
        <h2>{t('addMembersTitle')}</h2>
        <button type="button" className="btn" onClick={apply} disabled={busy || selected.length === 0}>{t('addMembersApply')} ({selected.length})</button>
        {onInvite && <button type="button" className="btn btn-ghost" onClick={onInvite}>{t('chatInfo:inviteLink')}</button>}
        {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
        {selected.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            {selected.map((u) => (
              <span key={u.id} className="chip">
                {u.displayName}
                <button onClick={() => toggle(u)}>✕</button>
              </span>
            ))}
          </div>
        )}
        <input
          className="input"
          placeholder={t('addMembersSearch')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
          style={{ marginBottom: 8 }}
        />
        {!q.trim() && list.length > 0 && (
          <div style={{ color: 'var(--muted)', fontSize: 12, margin: '4px 0' }}>{t('addMembersContacts')}</div>
        )}
        {list.length === 0 && (
          <div style={{ color: 'var(--muted)', fontSize: 13, margin: '4px 0 8px' }}>
            {q.trim() ? t('addMembersNoResults') : t('addMembersNoContacts')}
          </div>
        )}
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          {list.map((u) => {
            const on = selected.some((s) => s.id === u.id);
            return (
              <div key={u.id} className="user-row" onClick={() => toggle(u)}>
                <div className="avatar" style={{ width: 32, height: 32, fontSize: 13 }}>
                  {u.displayName[0]?.toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div>{u.displayName}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>@{u.username}</div>
                </div>
                <input type="checkbox" checked={on} readOnly />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
